// Import package components
import { Fragment, useMemo, useEffect, useContext, useRef, useState } from "react";

// Import base components
import { Button } from "../../../ui";
import { AppContexts } from "../../../providers";
import { groupTags } from "../../../utils/tag-groups";

const Tags = ({ value, onChange, readOnly = false }) => {
  const settingsContext = useContext(AppContexts.SettingsContext);

  const [searchValue, setSearchValue] = useState("");
  const [filteredTagValues, setFilteredTagValues] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const rootRef = useRef(null);

  const tagValues = useMemo(() => {
    if (!settingsContext.settings) {
      return [];
    } else {
      if (!settingsContext.settings.tags) {
        return [];
      } else {
        return settingsContext.settings.tags.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      }
    }
  }, [settingsContext.settings]);

  useEffect(() => {
    setFilteredTagValues(tagValues.filter(tag => tag.name.toLowerCase().includes(searchValue.toLowerCase())));
  }, [tagValues, searchValue]);

  const tagSections = useMemo(
    () => groupTags(filteredTagValues, settingsContext.settings?.tag_groups || []),
    [filteredTagValues, settingsContext.settings?.tag_groups],
  );

  const handleInputChange = (tagId, selected) => {
    if (selected) {
      onChange([...value, tagValues.find((tagValue) => tagValue.id === tagId)]);
    } else {
      onChange(value.filter((tag) => tag.id !== tagId));
    }
  };

  const openTags = (e) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeTags = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearchValue("");
      }
    };

    window.addEventListener("click", closeTags, true);
    return () => window.removeEventListener("click", closeTags, true);
  }, [isOpen]);

  return (
    <div className="uk-flex uk-flex-top" ref={rootRef} onClick={openTags}>
      <div>
        <Button>
          <img src="/images/tag.svg" alt="Select tags" />
        </Button>
        <div
          className={`uk-dropdown guest-tags-dropdown${isOpen ? " uk-open" : ""}`}
        >
          <div className="uk-search uk-search-default uk-margin-small-bottom">
            {searchValue && searchValue !== "" ? (
              <a
                className="uk-form-icon-flip uk-form-icon"
                uk-icon="icon: close"
                onClick={() => setSearchValue("")}
              ></a>
            ) : (
              <span
                className="uk-search-icon-flip"
                uk-search-icon="true"
              />
            )}
            <input
              className="uk-search-input"
              type="search"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <ul className="uk-nav uk-dropdown-nav tags-selector">
            {tagSections.map((section) => (
              <Fragment key={`tag_group_${section.id}`}>
                <li className="tag-group-heading">{section.label}</li>
                {section.tags.map((tagValue) => (
                  <li key={`tagValues_list_${section.id}_${tagValue.id}`}>
                    <label>
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        checked={
                          value?.findIndex((tag) => tag.id === tagValue.id) >= 0
                        }
                        onChange={
                          readOnly
                            ? () => {}
                            : (event) =>
                                handleInputChange(tagValue.id, event.target.checked)
                        }
                      />
                      {tagValue.name}
                    </label>
                  </li>
                ))}
              </Fragment>
            ))}
          </ul>
        </div>
      </div>
      <div className="hms-guest-tags">
        <div className="guest-tags">
          {value &&
            value.map((tagValue, index) => (
              <div className="uk-inline" key={`tag_div_${index}`}>
                {tagValue.name}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default Tags;
